/**
 * MessagesArea
 *
 * Top-level supplier Messages page. Adds a clear channel toggle between
 * in-app Idotoo messages and the external Email inbox, so suppliers always
 * know which channel they are looking at and which they are replying on.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Tabs, Tab, Chip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnreadMessages } from '../../context/UnreadMessagesContext';
import { useAuth } from '../../context/AuthContext';
import { canAccessPlatformPreviewFeatures } from '../../utils/platformPreviewAccess';
import ChatContainer from './ChatContainer';
import EmailInbox from './EmailInbox';

const CHANNELS = {
  messages: 'messages',
  email: 'email'
};

const MessagesArea = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { chatUnreadCount = 0, emailUnreadCount = 0, refreshUnreadMessagesCount } = useUnreadMessages();

  const { user } = useAuth();
  const showEmailInbox = canAccessPlatformPreviewFeatures(user);
  const params = new URLSearchParams(location.search);
  const queryChannel = params.get('channel');
  const initial =
    showEmailInbox && queryChannel === CHANNELS.email ? CHANNELS.email : CHANNELS.messages;

  const [channel, setChannel] = useState(initial);
  const [emailUnreadOverride, setEmailUnreadOverride] = useState(null);

  useEffect(() => {
    if (!showEmailInbox && queryChannel === CHANNELS.email) {
      const nextParams = new URLSearchParams(location.search);
      nextParams.delete('channel');
      navigate(
        { pathname: '/supplier/messages', search: nextParams.toString() },
        { replace: true }
      );
      setChannel(CHANNELS.messages);
      return;
    }
    setChannel(
      showEmailInbox && queryChannel === CHANNELS.email ? CHANNELS.email : CHANNELS.messages
    );
  }, [queryChannel, showEmailInbox, location.search, navigate]);

  const activeChannel =
    showEmailInbox && channel === CHANNELS.email ? CHANNELS.email : CHANNELS.messages;

  const emailUnreadForToggle = useMemo(
    () => (emailUnreadOverride === null ? emailUnreadCount : emailUnreadOverride),
    [emailUnreadCount, emailUnreadOverride]
  );

  const handleChange = (_event, value) => {
    if (!showEmailInbox && value === CHANNELS.email) {
      return;
    }
    setChannel(value);
    const nextParams = new URLSearchParams(location.search);
    if (value === CHANNELS.email) {
      nextParams.set('channel', 'email');
    } else {
      nextParams.delete('channel');
    }
    navigate({ pathname: '/supplier/messages', search: nextParams.toString() }, { replace: true });
  };

  return (
    <Box sx={{ width: '100%' }}>
      {showEmailInbox ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
        <Tabs
          value={activeChannel}
          onChange={handleChange}
          sx={{
            width: 'fit-content',
            minHeight: 48,
            py: 0.5,
            px: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
            borderRadius: '999px',
            '& .MuiTabs-flexContainer': {
              gap: 0.5
            },
            '& .MuiTabs-indicator': {
              height: 'calc(100% - 8px)',
              bottom: 4,
              borderRadius: '999px',
              backgroundColor: '#ffffff',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
              zIndex: 0
            },
            '& .MuiTab-root': {
              minHeight: 40,
              minWidth: 'unset',
              px: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '999px',
              zIndex: 1
            }
          }}
        >
          <Tab
            value={CHANNELS.messages}
            label={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Chats
                <Chip size="small" label={chatUnreadCount} />
              </Box>
            )}
          />
          <Tab
            value={CHANNELS.email}
            label={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Email inbox
                <Chip size="small" label={emailUnreadForToggle} />
              </Box>
            )}
          />
        </Tabs>
      </Box>
      ) : null}

      {activeChannel === CHANNELS.email ? (
        <EmailInbox
          onUnreadCountChange={(count) => {
            setEmailUnreadOverride(count);
            refreshUnreadMessagesCount();
          }}
        />
      ) : (
        <ChatContainer {...props} />
      )}
    </Box>
  );
};

export default MessagesArea;
